import React from 'react';
import type { TransformedReport, BannerEvaluationMetrics } from '../types';

interface BannerVariationSlideProps {
  data: TransformedReport;
  strategyIndex: number;
}

/** Metric display items mapping label to BannerEvaluationMetrics key. */
const METRIC_ITEMS: { label: string; key: keyof BannerEvaluationMetrics }[] = [
  { label: 'インプレッション', key: 'impression' },
  { label: '視認性', key: 'visibility' },
  { label: '理解度', key: 'comprehension' },
  { label: 'コピー', key: 'copy_quality' },
  { label: '行動促進', key: 'call_to_action' },
];

/**
 * BannerVariationSlide - Slides 13-15: Strategy name + 3 banner variations (larger display).
 * Each banner includes AI prediction scores and design intent.
 */
const BannerVariationSlide: React.FC<BannerVariationSlideProps> = ({ data, strategyIndex }) => {
  const strategies = data.appeal_strategies ?? [];
  const banners = data.banners ?? [];
  const strat = strategies[strategyIndex];

  // Get banner evaluation for this strategy
  const evaluation = data.banner_evaluations?.[strategyIndex];
  const overallScore = evaluation?.overall_score ?? null;
  const metrics = evaluation?.metrics ?? {};

  // Get 3 banners for this strategy
  const stratBanners = banners.slice(strategyIndex * 3, strategyIndex * 3 + 3);

  const stratName = strat?.name || `訴求軸 ${strategyIndex + 1}`;
  const stratDirection = strat?.direction || '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="slide-hdr">
        <p className="sl">Step 03</p>
        <h2>{stratName} x 3バリエーション</h2>
        <p className="bt">{stratDirection}</p>
      </div>

      <div className="banner-variation-grid">
        {[0, 1, 2].map((pi) => {
          const banner = stratBanners[pi];
          return (
            <div key={pi} style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(4px, 0.6vmin, 8px)' }}>
              {/* Banner image */}
              {banner?.image_url ? (
                <div style={{ borderRadius: 8, overflow: 'hidden', background: '#E4E0ED', flex: 1 }}>
                  <img
                    src={banner.image_url}
                    alt={`Pattern ${String.fromCharCode(65 + pi)}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              ) : (
                <div className="ph" style={{ borderRadius: 8, flex: 1 }}>
                  Pattern {String.fromCharCode(65 + pi)}
                </div>
              )}

              {/* AI prediction score card */}
              <div className="card" style={{ padding: 'clamp(4px, 0.6vmin, 8px)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700 }}>AI効果予測</span>
                  <span className="score-number" style={{ fontSize: 20, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                    {overallScore !== null ? overallScore.toFixed(1) : '\u2014'}
                    <span style={{ fontSize: 13, color: 'rgba(29,25,38,0.4)', fontWeight: 400 }}>/10</span>
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 11 }}>
                  {METRIC_ITEMS.map((m) => {
                    const value = metrics[m.key];
                    const widthPercent = value != null ? value * 10 : 50;
                    return (
                      <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 60, color: 'rgba(29,25,38,0.4)', flexShrink: 0, fontSize: 'clamp(9px, 1.2vmin, 11px)' }}>{m.label}</span>
                        <div style={{ flex: 1, height: 6, background: '#E4E0ED', borderRadius: 999 }}>
                          <div style={{ height: '100%', background: '#3eb298', borderRadius: 999, width: `${widthPercent}%`, transition: 'width 0.3s ease' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Design intent card */}
              <div className="card" style={{ padding: 'clamp(4px, 0.6vmin, 8px)' }}>
                <p style={{ fontSize: 11, fontWeight: 700, borderLeft: '2px solid #3eb298', paddingLeft: 8, marginBottom: 4 }}>制作意図</p>
                <p style={{ fontSize: 'clamp(9px, 1.2vmin, 11px)', color: 'rgba(29,25,38,0.6)', lineHeight: 1.6 }}>
                  <span style={{ color: 'rgba(29,25,38,0.4)', fontWeight: 600 }}>セグメント</span> {data.segments?.[0]?.name || ''}<br />
                  <span style={{ color: 'rgba(29,25,38,0.4)', fontWeight: 600 }}>訴求</span> {stratDirection}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BannerVariationSlide;
